import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Check,
  X,
} from "@phosphor-icons/react";

import ContactFormFields, {
  getContactFormValidationError,
} from "@/components/commerce/ContactFormFields";
import { formatApiError } from "@/lib/api";


const CREATE_CLIENT_OPTION = "__create_client__";
const CREATE_MACHINE_OPTION = "__create_machine__";

const EMPTY_FORM = {
  prospect_id: "",
  machine_id: "",
  machine_name: "",
  desired_date: "",
  site_name: "",
  address: "",
  postal_code: "",
  city: "",
  country: "FR",
  notes: "",
};

const EMPTY_QUICK_CLIENT_FORM = {
  company: "",
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  position: "",
  address: "",
  postalCode: "",
  city: "",
  country: "FR",
  lat: null,
  lng: null,
  lifecycleStage: "client",
};


function contactLabel(contact) {
  const contactName = [
    contact.firstName,
    contact.lastName,
  ]
    .filter(Boolean)
    .join(" ");

  if (
    contact.company
    && contactName
  ) {
    return `${contact.company} — ${contactName}`;
  }

  return (
    contact.company
    || contactName
    || contact.email
    || contact.id
  );
}


function machineLabel(machine) {
  const identity = [
    machine.name,
    machine.model,
  ]
    .filter(Boolean)
    .join(" — ");

  return machine.serialNumber
    ? `${identity || "Équipement"} · ${machine.serialNumber}`
    : identity || machine.id;
}


function machineName(machine) {
  return [
    machine?.name,
    machine?.model,
  ]
    .filter(Boolean)
    .join(" — ");
}


function contactAddress(contact) {
  return {
    site_name:
      contact?.siteName
      || contact?.company
      || "",
    address:
      contact?.address
      || "",
    postal_code:
      contact?.postalCode
      || contact?.postal_code
      || "",
    city:
      contact?.city
      || "",
    country:
      ["FR", "BE"].includes(
        contact?.country
      )
        ? contact.country
        : "FR",
  };
}


export default function InstallationCreateModal({
  open,
  clients = [],
  prospects = [],
  machines = [],
  selectedMachineId = "",
  loading = false,
  saving = false,
  error = "",
  onClose,
  onSubmit,
  onCreateClient,
  onRequestCreateMachine,
}) {
  const [form, setForm] =
    useState(EMPTY_FORM);

  const [
    quickClientOpen,
    setQuickClientOpen,
  ] = useState(false);

  const [
    quickClientForm,
    setQuickClientForm,
  ] = useState(
    EMPTY_QUICK_CLIENT_FORM
  );

  const [
    quickClientSaving,
    setQuickClientSaving,
  ] = useState(false);

  const [
    quickClientError,
    setQuickClientError,
  ] = useState("");

  useEffect(() => {
    if (!open) {
      return;
    }

    setForm(EMPTY_FORM);
    setQuickClientOpen(false);
    setQuickClientForm(
      EMPTY_QUICK_CLIENT_FORM
    );
    setQuickClientError("");
  }, [open]);

  useEffect(() => {
    if (
      !open
      || !selectedMachineId
    ) {
      return;
    }

    const machine = machines.find(
      (item) =>
        item.id === selectedMachineId
    );

    if (!machine) {
      return;
    }

    setForm((current) => ({
      ...current,
      machine_id: machine.id,
      machine_name:
        machineName(machine),
    }));
  }, [
    open,
    machines,
    selectedMachineId,
  ]);

  const sortedClients = useMemo(
    () =>
      [...clients].sort(
        (left, right) =>
          contactLabel(left)
            .localeCompare(
              contactLabel(right),
              "fr",
              {
                sensitivity: "base",
              }
            )
      ),
    [clients]
  );

  const sortedProspects = useMemo(
    () =>
      [...prospects].sort(
        (left, right) =>
          contactLabel(left)
            .localeCompare(
              contactLabel(right),
              "fr",
              {
                sensitivity: "base",
              }
            )
      ),
    [prospects]
  );

  const sortedMachines = useMemo(
    () =>
      [...machines].sort(
        (left, right) =>
          machineLabel(left)
            .localeCompare(
              machineLabel(right),
              "fr",
              {
                sensitivity: "base",
              }
            )
      ),
    [machines]
  );

  if (!open) {
    return null;
  }

  const updateField = (
    field,
    value
  ) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const selectContact = (
    prospectId,
    contact = null
  ) => {
    const selected =
      contact
      || clients.find(
        (item) =>
          item.id === prospectId
      )
      || prospects.find(
        (item) =>
          item.id === prospectId
      );

    setForm((current) => ({
      ...current,
      prospect_id: prospectId,
      ...contactAddress(selected),
    }));
  };

  const handleContactChange = (
    value
  ) => {
    setQuickClientError("");

    if (
      value ===
      CREATE_CLIENT_OPTION
    ) {
      selectContact("");
      setQuickClientOpen(true);
      return;
    }

    setQuickClientOpen(false);
    selectContact(value);
  };

  const selectMachine = (
    machineId
  ) => {
    const machine = machines.find(
      (item) =>
        item.id === machineId
    );

    setForm((current) => ({
      ...current,
      machine_id: machineId,
      machine_name:
        machineId
          ? machineName(machine)
          : "",
    }));
  };

  const handleMachineChange = (
    value
  ) => {
    if (
      value ===
      CREATE_MACHINE_OPTION
    ) {
      selectMachine("");
      onRequestCreateMachine?.();
      return;
    }

    selectMachine(value);
  };

  const createQuickClient =
    async () => {
      if (
        !onCreateClient
        || quickClientSaving
      ) {
        return;
      }

      const validationError =
        getContactFormValidationError(
          quickClientForm
        );

      if (validationError) {
        setQuickClientError(
          validationError
        );
        return;
      }

      setQuickClientSaving(true);
      setQuickClientError("");

      try {
        const created =
          await onCreateClient({
            company:
              quickClientForm
                .company
                .trim(),
            first_name:
              quickClientForm
                .firstName
                .trim(),
            last_name:
              quickClientForm
                .lastName
                .trim(),
            email:
              quickClientForm
                .email
                .trim(),
            phone:
              quickClientForm
                .phone
                .trim(),
            position:
              quickClientForm
                .position
                .trim(),
            address:
              quickClientForm
                .address
                .trim(),
            postal_code:
              quickClientForm
                .postalCode
                .trim(),
            city:
              quickClientForm
                .city
                .trim(),
            country:
              quickClientForm
                .country
              || "FR",
          });

        if (!created?.id) {
          throw new Error(
            "Client créé sans identifiant exploitable."
          );
        }

        selectContact(
          created.id,
          created
        );

        setQuickClientForm(
          EMPTY_QUICK_CLIENT_FORM
        );
        setQuickClientOpen(false);
      } catch (requestError) {
        setQuickClientError(
          formatApiError(
            requestError
              .response
              ?.data
              ?.detail
          )
          || requestError.message
          || "Impossible de créer le client."
        );
      } finally {
        setQuickClientSaving(false);
      }
    };

  const submit = (event) => {
    event.preventDefault();

    if (
      !form.prospect_id
      || !form.machine_id
      || !form.machine_name.trim()
      || saving
    ) {
      return;
    }

    onSubmit({
      ...form,
      machine_name:
        form.machine_name.trim(),
      site_name:
        form.site_name.trim(),
      address:
        form.address.trim(),
      postal_code:
        form.postal_code.trim(),
      city:
        form.city.trim(),
      notes:
        form.notes.trim(),
      desired_date:
        form.desired_date || null,
    });
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center acta-modal-overlay p-3 sm:p-6 backdrop-blur-sm"
      data-testid="installation-create-modal"
      onMouseDown={
        saving
          ? undefined
          : onClose
      }
    >
      <div
        className="acta-modal-surface max-h-[94vh] w-full max-w-3xl overflow-y-auto rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] shadow-2xl"
        onMouseDown={
          (event) =>
            event.stopPropagation()
        }
      >
        <div className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4 py-4 sm:px-6">
          <div>
            <div className="label-eyebrow">
              Installation manuelle
            </div>

            <h2 className="mt-1 font-heading text-xl font-black tracking-tight">
              Nouvelle installation
            </h2>

            <p className="mt-1 text-sm text-[var(--do-muted)]">
              Créez une installation sans affaire ou devis d’origine.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            aria-label="Fermer"
            className="rounded-lg p-2 transition hover:bg-[var(--do-surface-soft)] disabled:opacity-50"
          >
            <X size={20} />
          </button>
        </div>

        <form
          onSubmit={submit}
          className="space-y-6 p-4 sm:p-6"
        >
          {error && (
            <div
              className="rounded-xl border border-[var(--do-danger)] bg-[var(--do-danger-soft)] p-3 text-sm font-semibold"
              role="alert"
              data-testid="installation-create-error"
            >
              {error}
            </div>
          )}

          <section className="space-y-4">
            <div>
              <div className="label-eyebrow">
                Client et matériel
              </div>

              <p className="mt-1 text-xs text-[var(--do-muted)]">
                Sélectionnez un client, créez-en un ou choisissez un prospect qui deviendra client. Sélectionnez ensuite une machine en stock ou ajoutez-en une.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <label className="space-y-1.5">
                <span className="label-eyebrow block">
                  Client *
                </span>

                <select
                  value={form.prospect_id}
                  onChange={
                    (event) =>
                      handleContactChange(
                        event.target.value
                      )
                  }
                  disabled={
                    loading
                    || saving
                    || quickClientSaving
                  }
                  required
                  data-testid="installation-create-client"
                  className="h-11 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 text-sm outline-none focus:border-[var(--do-accent)] disabled:opacity-60"
                >
                  <option value="">
                    {loading
                      ? "Chargement des contacts…"
                      : "Sélectionner un contact"}
                  </option>

                  <optgroup label="Clients">
                    {sortedClients.map(
                      (client) => (
                        <option
                          key={client.id}
                          value={client.id}
                        >
                          {contactLabel(
                            client
                          )}
                        </option>
                      )
                    )}
                  </optgroup>

                  <optgroup label="Créer un nouveau client">
                    <option
                      value={
                        CREATE_CLIENT_OPTION
                      }
                    >
                      + Créer un nouveau client
                    </option>
                  </optgroup>

                  <optgroup label="Prospects — deviendra client">
                    {sortedProspects.map(
                      (prospect) => (
                        <option
                          key={prospect.id}
                          value={
                            prospect.id
                          }
                        >
                          {contactLabel(
                            prospect
                          )}
                        </option>
                      )
                    )}
                  </optgroup>
                </select>
              </label>

              <label className="space-y-1.5">
                <span className="label-eyebrow block">
                  Machine en stock *
                </span>

                <select
                  value={form.machine_id}
                  onChange={
                    (event) =>
                      handleMachineChange(
                        event.target.value
                      )
                  }
                  disabled={
                    loading
                    || saving
                  }
                  required
                  data-testid="installation-create-stock-machine"
                  className="h-11 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 text-sm outline-none focus:border-[var(--do-accent)] disabled:opacity-60"
                >
                  <option value="">
                    Sélectionner une machine en stock
                  </option>

                  <optgroup label="Machines en stock">
                    {sortedMachines.map(
                      (machine) => (
                        <option
                          key={machine.id}
                          value={machine.id}
                        >
                          {machineLabel(
                            machine
                          )}
                        </option>
                      )
                    )}
                  </optgroup>

                  <optgroup label="Ajouter une machine">
                    <option
                      value={
                        CREATE_MACHINE_OPTION
                      }
                    >
                      + Ajouter une machine au stock
                    </option>
                  </optgroup>
                </select>
              </label>
            </div>

            {quickClientOpen && (
              <section
                className="rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-2)] p-4"
                data-testid="installation-create-client-form"
                onKeyDown={(event) => {
                  if (
                    event.key === "Enter"
                  ) {
                    event.preventDefault();
                    createQuickClient();
                  }
                }}
              >
                <div className="mb-3">
                  <div className="font-heading text-sm font-black">
                    Nouveau client
                  </div>

                  <p className="mt-1 text-xs text-[var(--do-muted)]">
                    Le client sera créé puis sélectionné pour cette installation.
                  </p>
                </div>

                {quickClientError ? (
                  <div
                    className="mb-3 rounded-lg border border-[var(--do-danger-border)] bg-[var(--do-danger-soft)] p-3 text-xs text-[var(--do-danger)]"
                    role="alert"
                  >
                    {quickClientError}
                  </div>
                ) : null}

                <ContactFormFields
                  value={
                    quickClientForm
                  }
                  onChange={
                    setQuickClientForm
                  }
                  showLifecycleStage={
                    false
                  }
                />

                <div className="mt-4 flex justify-end">
                  <button
                    type="button"
                    onClick={
                      createQuickClient
                    }
                    disabled={
                      saving
                      || quickClientSaving
                    }
                    className="rounded-lg bg-[var(--do-accent)] px-4 py-2.5 text-sm font-heading font-bold text-[var(--do-on-accent)] disabled:opacity-50"
                    data-testid="installation-create-client-submit"
                  >
                    {quickClientSaving
                      ? "Création…"
                      : "Créer et sélectionner"}
                  </button>
                </div>
              </section>
            )}

            <label className="block space-y-1.5">
              <span className="label-eyebrow block">
                Date souhaitée
              </span>

              <input
                type="date"
                value={
                  form.desired_date
                }
                onChange={
                  (event) =>
                    updateField(
                      "desired_date",
                      event.target.value
                    )
                }
                disabled={saving}
                data-testid="installation-create-desired-date"
                className="h-11 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 text-sm outline-none focus:border-[var(--do-accent)] disabled:opacity-60"
              />
            </label>
          </section>

          <section className="space-y-4 border-t border-[var(--do-border)] pt-5">
            <div>
              <div className="label-eyebrow">
                Implantation
              </div>

              <p className="mt-1 text-xs text-[var(--do-muted)]">
                Ces informations sont préremplies depuis le contact sélectionné et restent modifiables.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <label className="space-y-1.5">
                <span className="label-eyebrow block">
                  Site
                </span>

                <input
                  type="text"
                  value={form.site_name}
                  onChange={
                    (event) =>
                      updateField(
                        "site_name",
                        event.target.value
                      )
                  }
                  disabled={saving}
                  maxLength={240}
                  className="h-11 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 text-sm outline-none focus:border-[var(--do-accent)] disabled:opacity-60"
                />
              </label>

              <label className="space-y-1.5">
                <span className="label-eyebrow block">
                  Adresse
                </span>

                <input
                  type="text"
                  value={form.address}
                  onChange={
                    (event) =>
                      updateField(
                        "address",
                        event.target.value
                      )
                  }
                  disabled={saving}
                  maxLength={320}
                  className="h-11 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 text-sm outline-none focus:border-[var(--do-accent)] disabled:opacity-60"
                />
              </label>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <label className="space-y-1.5">
                <span className="label-eyebrow block">
                  Code postal
                </span>

                <input
                  type="text"
                  value={
                    form.postal_code
                  }
                  onChange={
                    (event) =>
                      updateField(
                        "postal_code",
                        event.target.value
                      )
                  }
                  disabled={saving}
                  maxLength={32}
                  className="h-11 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 text-sm outline-none focus:border-[var(--do-accent)] disabled:opacity-60"
                />
              </label>

              <label className="space-y-1.5">
                <span className="label-eyebrow block">
                  Ville
                </span>

                <input
                  type="text"
                  value={form.city}
                  onChange={
                    (event) =>
                      updateField(
                        "city",
                        event.target.value
                      )
                  }
                  disabled={saving}
                  maxLength={180}
                  className="h-11 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 text-sm outline-none focus:border-[var(--do-accent)] disabled:opacity-60"
                />
              </label>

              <label className="space-y-1.5">
                <span className="label-eyebrow block">
                  Pays
                </span>

                <select
                  value={form.country}
                  onChange={
                    (event) =>
                      updateField(
                        "country",
                        event.target.value
                      )
                  }
                  disabled={saving}
                  className="h-11 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 text-sm outline-none focus:border-[var(--do-accent)] disabled:opacity-60"
                >
                  <option value="FR">
                    France
                  </option>
                  <option value="BE">
                    Belgique
                  </option>
                </select>
              </label>
            </div>
          </section>

          <label className="block space-y-1.5">
            <span className="label-eyebrow block">
              Notes
            </span>

            <textarea
              value={form.notes}
              onChange={
                (event) =>
                  updateField(
                    "notes",
                    event.target.value
                  )
              }
              disabled={saving}
              rows={3}
              maxLength={4000}
              placeholder="Informations utiles à la préparation"
              className="w-full resize-none rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2 text-sm outline-none focus:border-[var(--do-accent)] disabled:opacity-60"
            />
          </label>

          <div className="flex flex-col-reverse gap-2 border-t border-[var(--do-border)] pt-5 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="rounded-lg border border-[var(--do-border)] px-4 py-2.5 text-sm font-heading font-semibold transition hover:bg-[var(--do-surface-soft)] disabled:opacity-50"
            >
              Annuler
            </button>

            <button
              type="submit"
              disabled={
                loading
                || saving
                || quickClientSaving
                || !form.prospect_id
                || !form.machine_id
                || !form.machine_name
                  .trim()
              }
              data-testid="installation-create-submit"
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-[var(--do-accent)] px-4 py-2.5 text-sm font-heading font-bold text-[var(--do-on-accent)] transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Check
                size={18}
                weight="bold"
              />

              {saving
                ? "Création…"
                : "Créer l’installation"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
